import { Subscription } from 'rxjs';
import { Editor, EditorModule } from 'primeng/editor';
import { ViewHtmlButtonDirective } from '../../../directives/view-html-button.directive';
import { EmojiPickerButtonComponent } from '../../emoji-picker-button/emoji-picker-button.component';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OrdersService } from '../../../api/orders.service';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { FuseTranslationLoaderService } from '../../../../../@fuse/services/translation-loader.service';
import { GT2PageAbstract } from '../../../content/abstract/GT2PageAbstract';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';
import { LabourChargeService } from '../../../api/labour-charge.service';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ValidatorHelper } from '../../../validators/ValidatorHelper';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Gt2PrimengModule } from '../../../modules/gt2-primeng.module';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { Gt2SelectComponent } from '../../gt2-select/gt2-select.component';
import { FuseDirectivesModule } from '../../../../../@fuse/directives/directives';
import { FusePipesModule } from '../../../../../@fuse/pipes/pipes.module';
import { CalendarDateFormatter } from 'angular-calendar';
import { CustomDateFormatter } from '../../../content/calendar/CustomDateFormatter';
import { GT2DateAdapter } from '../../../utils/GT2DateAdapter';
import { AddLabourChargePanelService } from '../../../services/add-labour-charge-panel.service';
import { EditLabourChargePanelService } from '../../../services/edit-labour-charge-panel.service';

@Component({
  selector: 'app-create-labour-charge',
  templateUrl: './create-labour-charge.component.html',
  styleUrls: ['./create-labour-charge.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FuseDirectivesModule,
    FusePipesModule,
    MatButtonModule,
    TranslateModule,
    EditorModule,
    ViewHtmlButtonDirective,
    EmojiPickerButtonComponent,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    FormsModule,
    Gt2PrimengModule,
    Gt2SelectComponent,
    NgxMaterialTimepickerModule,
  ],
  providers: [
    LabourChargeService,
    { provide: DateAdapter, useClass: GT2DateAdapter },
    { provide: CalendarDateFormatter, useClass: CustomDateFormatter },
  ],
})
export class CreateLabourChargeComponent
  extends GT2PageAbstract
  implements OnInit, AfterViewInit, OnDestroy
{
  static CREATE: string = 'CREATE';
  static EDIT: string = 'EDIT';

  mode: string = CreateLabourChargeComponent.CREATE;
  orderUUID!: string;
  override loading: boolean = false;
  autoRedirect: boolean = false;
  form: UntypedFormGroup;
  selectedLabourCharge: any;
  labourChargeToUpdate: any;
  @ViewChild('noteEditor') noteEditor?: Editor;
  noteEditorSubscription?: Subscription;

  constructor(
    private addPanelService: AddLabourChargePanelService,
    private editPanelService: EditLabourChargePanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    public orderService: OrdersService,
    public dialog: MatDialog,
    public router: Router,
    private adapter: DateAdapter<any>,
    private formBuilder: UntypedFormBuilder,
    private toastr: ToastrService,
    public labourChargeService: LabourChargeService,
    private logger: NGXLogger,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    this.adapter.setLocale(this.translate.currentLang);

    this.form = this.formBuilder.group({
      unit_price: [
        '',
        [ValidatorHelper.isNumber0To1000000, Validators.required],
      ],
      qty: ['', [ValidatorHelper.isNumber0To1000000, Validators.required]],
      from_date: ['', [ValidatorHelper.dateRangeDefault]],
      to_date: ['', [ValidatorHelper.dateRangeDefault]],
    });

    const editData = this.editPanelService.getData();
    const addData = this.addPanelService.getData();

    if (editData) {
      this.mode = CreateLabourChargeComponent.EDIT;
      this.orderUUID = editData.orderUUID;
      this.labourChargeToUpdate = editData.labourChargeToUpdate;
      this.autoRedirect = editData.autoRedirect ?? false;
    } else if (addData) {
      this.mode = CreateLabourChargeComponent.CREATE;
      this.orderUUID = addData.orderUUID;
      this.autoRedirect = addData.autoRedirect ?? false;
    }
  }

  ngOnInit() {}

  ngAfterViewInit() {}

  ngOnDestroy() {
    if (this.noteEditorSubscription) {
      this.noteEditorSubscription.unsubscribe();
    }
  }

  public onSubmitLabourCharge(addAnother: boolean = false) {
    this.loading = true;

    if (this.mode === CreateLabourChargeComponent.CREATE) {
      this.doCreateLabourCharge(addAnother);
    } else if (this.mode === CreateLabourChargeComponent.EDIT) {
      this.updateLabourCharge();
    }
  }

  public doCreateLabourCharge(addAnother: boolean = false) {
    this.orderService
      .addLabourCharge(this.orderUUID, {
        staffs: [this.selectedLabourCharge],
      })
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          if (addAnother) {
            this.addPanelService.emitIntermediateResult(response.data);
            this.toastr.success(
              '',
              this.translate.instant('LABOUR_CHARGE.CREATED'),
            );
            this.selectedLabourCharge = null;
          } else {
            this.addPanelService.emitResult(response.data);
          }
        },
        error: (error: any) => {
          this.logger.error(
            'CreateLabourChargeComponent.doCreateLabourCharge() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.loading = false;
          this.handleAPIError(
            error,
            this.dialog,
            this.toastr,
            this.translate.instant('GENERIC.ERRORS.GENERIC') + ' CL001',
            this.router,
          );
        },
      });
  }

  public updateLabourCharge(): void {
    this.orderService
      .updateLabourCharges(this.orderUUID, [this.labourChargeToUpdate])
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          this.editPanelService.emitResult(response.data);
        },
        error: (error: any) => {
          this.logger.error(
            'CreateLabourChargeComponent.updateLabourCharge() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.loading = false;
          this.handleAPIError(
            error,
            this.dialog,
            this.toastr,
            this.translate.instant('GENERIC.ERRORS.GENERIC') + ' CL002',
            this.router,
          );
        },
      });
  }

  public onLabourChargeChange(event: any): void {
    this.selectedLabourCharge = event;
  }

  public close(): void {
    if (this.mode === CreateLabourChargeComponent.EDIT) {
      this.editPanelService.emitResult(false);
    } else {
      this.addPanelService.emitResult(false);
    }
  }

  public openStartTimePicker(): void {}
  public openEndTimePicker(): void {}
}
